<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="tela pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Contas a Pagar</v-toolbar-title>
                    <v-spacer/>
                    <v-btn color="primary" fab small @click="openCadastro">
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <v-row dense>
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field
                                    label="Mês de referência"
                                    v-model="filtros.mes_referencia"
                                    type="month"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    @change="consultarTudo"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-select
                                    label="Conta"
                                    v-model="filtros.conta_id"
                                    :items="contas"
                                    item-text="nome"
                                    item-value="id"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    @change="consultarTudo"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-select
                                    label="Categoria"
                                    v-model="filtros.categoria_id"
                                    :items="categorias"
                                    item-text="nome"
                                    item-value="id"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    @change="consultarTudo"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-select
                                    label="Status"
                                    v-model="filtros.status"
                                    :items="statusOpcoes"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    @change="consultarTudo"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-row class="ma-1" dense>
                    <v-col cols="12" md="8">
                        <v-card elevation="2" class="fill-height">
                            <v-card-text>
                                <v-data-table
                                    :headers="headers"
                                    :items="dados"
                                    :loading="loading"
                                    :mobileBreakpoint="0"
                                    @click:row="editar"
                                    :items-per-page="20"
                                >
                                    <template v-slot:item.valor="{ item }">
                                        {{ formatCurrency(item.valor) }}
                                    </template>
                                    <template v-slot:item.mes_referencia="{ item }">
                                        {{ formatMes(item.mes_referencia) }}
                                    </template>
                                    <template v-slot:item.vencimento="{ item }">
                                        {{ formatDate(item.vencimento) }}
                                    </template>
                                    <template v-slot:item.pagamento="{ item }">
                                        <span v-if="item.pagamento">{{ formatDate(item.pagamento) }}</span>
                                        <span v-else class="grey--text">—</span>
                                    </template>
                                    <template v-slot:item.status_efetivo="{ item }">
                                        <v-chip
                                            small
                                            :color="statusColor(item.status_efetivo)"
                                            dark
                                        >
                                            {{ item.status_efetivo }}
                                        </v-chip>
                                    </template>
                                    <template v-slot:item.categoria="{ item }">
                                        <v-chip x-small :color="item.categoria && item.categoria.cor" dark v-if="item.categoria">
                                            {{ item.categoria.nome }}
                                        </v-chip>
                                    </template>
                                    <template v-slot:item.conta="{ item }">
                                        {{ item.conta && item.conta.nome }}
                                    </template>
                                    <template v-slot:item.actions="{ item }">
                                        <v-btn icon small @click.stop="excluir(item)">
                                            <v-icon small color="error">mdi-delete</v-icon>
                                        </v-btn>
                                    </template>
                                </v-data-table>
                                <div class="text-right pa-3">
                                    <strong>Total filtrado:</strong> {{ formatCurrency(totalFiltrado) }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-card v-for="grupo in agrupado" :key="grupo.conta_id" elevation="2" class="mb-3">
                            <v-card-title class="text-subtitle-1">
                                {{ grupo.conta_nome }}
                                <v-spacer/>
                                <span class="caption">{{ formatCurrency(grupo.total) }}</span>
                            </v-card-title>
                            <v-card-text>
                                <PieChart
                                    :labels="grupo.categorias.map(c => c.categoria_nome)"
                                    :data="grupo.categorias.map(c => c.total)"
                                    :colors="grupo.categorias.map(c => c.cor)"
                                    :height="200"
                                    doughnut
                                />
                            </v-card-text>
                        </v-card>
                        <v-card v-if="!agrupado.length" elevation="2">
                            <v-card-text class="text-center grey--text">
                                Sem dados para exibir
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <LancamentoForm
                    :visible="visible"
                    :formData="form"
                    :contas="contas"
                    :categorias="categorias"
                    @onCadastro="onCadastro"
                    @close="close"
                />
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import PieChart from '@/components/charts/PieChart';
import LancamentoForm from './LancamentoForm';
import moment from 'moment';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export default {
    name: 'ContasPagar',

    components: {
        BaseContainer,
        PieChart,
        LancamentoForm,
    },

    data: () => ({
        loading: false,
        visible: false,
        dados: [],
        agrupado: [],
        contas: [],
        categorias: [],
        form: {},
        filtros: {
            mes_referencia: moment().format('YYYY-MM'),
            conta_id: null,
            categoria_id: null,
            status: null,
        },
        statusOpcoes: [
            { text: 'Pendente', value: 'pendente' },
            { text: 'Pago', value: 'pago' },
            { text: 'Atrasado', value: 'atrasado' },
        ],
        headers: [
            { text: 'Nome', value: 'nome' },
            { text: 'Categoria', value: 'categoria', sortable: false },
            { text: 'Conta', value: 'conta', sortable: false },
            { text: 'Mês Ref.', value: 'mes_referencia', width: 100 },
            { text: 'Vencimento', value: 'vencimento', width: 110 },
            { text: 'Pagamento', value: 'pagamento', width: 110 },
            { text: 'Valor', value: 'valor', width: 110, align: 'end' },
            { text: 'Status', value: 'status_efetivo', width: 110, align: 'center' },
            { text: '', value: 'actions', width: 50, sortable: false, align: 'end' },
        ],
    }),

    computed: {
        totalFiltrado() {
            return this.dados.reduce((acc, l) => acc + Number(l.valor || 0), 0);
        },
    },

    mounted() {
        this.consultarAuxiliares().then(() => this.consultarTudo());
    },

    methods: {
        async consultarAuxiliares() {
            const [contasRes, catsRes] = await Promise.all([
                this.$http.get('financeiro-interno/contas'),
                this.$http.get('financeiro-interno/categorias'),
            ]);
            this.contas = (contasRes.data.data || []).filter(c => c.ativo);
            this.categorias = (catsRes.data.data || []).filter(c => c.ativo);
        },

        consultarTudo() {
            this.consultar();
            this.consultarAgrupado();
        },

        consultar() {
            this.loading = true;
            this.dados = [];

            const params = this.buildParams();

            this.$http.get('financeiro-interno/lancamentos', { params })
                .then(resp => { this.dados = resp.data.data; })
                .catch(() => alert('Não foi possível carregar os lançamentos'))
                .finally(() => (this.loading = false));
        },

        consultarAgrupado() {
            const params = {};
            if (this.filtros.mes_referencia) {
                params.mes_referencia = `${this.filtros.mes_referencia}-01`;
            }

            this.$http.get('financeiro-interno/lancamentos/agrupado', { params })
                .then(resp => { this.agrupado = resp.data.data; })
                .catch(() => { this.agrupado = []; });
        },

        buildParams() {
            const p = {};
            if (this.filtros.mes_referencia) p.mes_referencia = `${this.filtros.mes_referencia}-01`;
            if (this.filtros.conta_id) p.conta_id = this.filtros.conta_id;
            if (this.filtros.categoria_id) p.categoria_id = this.filtros.categoria_id;
            if (this.filtros.status) p.status = this.filtros.status;
            return p;
        },

        openCadastro() {
            const mesRef = this.filtros.mes_referencia
                ? `${this.filtros.mes_referencia}-01`
                : moment().format('YYYY-MM-01');
            this.form = {
                mes_referencia: mesRef,
                vencimento: moment(mesRef).endOf('month').format('YYYY-MM-DD'),
            };
            this.visible = true;
        },

        editar(row) {
            this.form = JSON.parse(JSON.stringify(row));
            this.visible = true;
        },

        close() {
            this.visible = false;
            this.form = {};
        },

        onCadastro() {
            this.close();
            this.consultarTudo();
        },

        excluir(item) {
            if (!confirm(`Excluir o lançamento "${item.nome}"?`)) return;
            this.$http.delete(`financeiro-interno/lancamentos/${item.id}`)
                .then(() => this.consultarTudo())
                .catch(e => alert(e?.response?.data?.message || 'Erro ao excluir'));
        },

        formatCurrency(v) {
            return brl.format(Number(v) || 0);
        },
        formatDate(v) {
            return v ? moment(v).format('DD/MM/YYYY') : '';
        },
        formatMes(v) {
            return v ? moment(v).format('MM/YYYY') : '';
        },
        statusColor(s) {
            return { pago: 'success', atrasado: 'error', pendente: 'warning' }[s] || 'grey';
        },
    },
};
</script>

<style scoped>
.tela {
    min-height: 100vh;
}
</style>
